import { Component, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ColumnMode, SelectionType } from '@swimlane/ngx-datatable';
import { BehaviorSubject } from 'rxjs';

import { environment } from 'src/environments/environment';
import { Tenant, Profile } from 'src/models';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';

import { CreateTenantModalComponent } from './create-tenant-modal/create-tenant-modal.component';

@Component({
  selector: 'app-super-admin-tenants',
  templateUrl: './super-admin-tenants.component.html',
  styleUrls: ['./super-admin-tenants.component.scss'],
})
export class SuperAdminTenantsComponent implements OnInit {
  public readonly tenants$: BehaviorSubject<Partial<Tenant>[]> = new BehaviorSubject([]);
  public readonly tenantUsers$: BehaviorSubject<Partial<Profile>[]> = new BehaviorSubject([]);
  public isLoading = true;
  public selectedTenant: Partial<Tenant>;
  public userSearchString = '';
  public billing: string;

  public ColumnMode = ColumnMode;
  public SelectionType = SelectionType;
  public selected = [];

  constructor(
    private tenantsService: TenantsService,
    private authService: AuthService,
    private msgService: MessageService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
  ) {}

  async ngOnInit() {
    await this.loadTenants();

    this.isLoading = false;
  }

  async loadTenants() {
    const res = await this.tenantsService.list({
      limit: null,
    });

    this.tenants$.next(res.data);
  }

  async loadUsersForTenant() {
    const res = await this.tenantsService.listUsers(this.selectedTenant?.id || null, {
      limit: null,
      order: ['name'],
      descending: false,
      where: this.userSearchString
        ? [
            {
              field: 'name',
              operator: 'like',
              value: this.userSearchString,
            },
          ]
        : undefined,
    });

    this.tenantUsers$.next(res.data);
  }

  async onSelect(event) {
    this.selectedTenant = await this.tenantsService.getTenantById(event.selected[0].id);
    this.userSearchString = '';
    this.billing = JSON.stringify(this.selectedTenant.billing);

    await this.loadUsersForTenant();
  }

  async signInAsUser(userId) {
    const customToken = await this.tenantsService.generateCustomToken(this.selectedTenant.id, userId);
    const origin = `https://${this.selectedTenant.subdomain}.${environment.rootDomain}`;

    // NOTE: Passing token using postMessage to avoid exposing it in the URL.
    window.open(`${origin}/login/impersonate`, '_blank');

    this.authService.sendCustomToken(customToken, origin);
  }

  async createTenant() {
    const modal = await this.modalCtrl.create({
      component: CreateTenantModalComponent,
      componentProps: {},
    });

    await modal.present();

    modal.onDidDismiss().then(() => {
      this.loadTenants();
    });
  }

  // This is needed for the case when a user is initially created for a new client using username/password for
  // demo purposes, then when they convert they want to set up SSO. Once SSO is set up, that initial user will
  // need to be reset in firebase or they may not be able to login depending on their SSO settings, due to some
  // strange behavior in firebase that we could not find another fix for.
  async resetFirebaseSettings(userId: string) {
    const confirm = await this.alertCtrl.create({
      header: 'Reset the Firebase settings for this user?',
      message:
        "This will force unlink the user's Firebase settings. When they log in again they will be recreated in Firebase and relinked with a new Firebase ID.",
      buttons: [
        {
          text: 'No',
          handler: () => {},
        },
        {
          text: 'Yes',
          handler: async () => {
            await this.tenantsService.resetFirebaseSettingsForUser(this.selectedTenant.id, userId);
          },
        },
      ],
    });

    await confirm.present();

    confirm.onDidDismiss().then(() => {
      setTimeout(() => {
        this.loadUsersForTenant();
      }, 500);
    });
  }

  async verifyEmailAddress(userId: string) {
    const confirm = await this.alertCtrl.create({
      header: `Manually verify this users's email address?`,
      message:
        "This will manually verify the user's email address and allow them to log in via email and password. Only do this if you are sure the user is who they say they are.",
      buttons: [
        {
          text: 'No',
          handler: () => {},
        },
        {
          text: 'Yes',
          handler: async () => {
            await this.tenantsService.verifyEmailAddressForUser(this.selectedTenant.id, userId);
          },
        },
      ],
    });

    await confirm.present();

    confirm.onDidDismiss().then(() => {
      setTimeout(() => {
        this.loadUsersForTenant();
      }, 500);
    });
  }

  async updateTenant() {
    this.isLoading = true;

    try {
      await this.tenantsService.updateTenantById(this.selectedTenant.id, {
        accountStatus: this.selectedTenant.accountStatus,
        trialEndDate: this.selectedTenant.trialEndDate,
        autoSplittingEnabled: this.selectedTenant.autoSplittingEnabled,
        moveToNeedsResponse: this.selectedTenant.moveToNeedsResponse,
        doNotReopenConversationsOnReply: this.selectedTenant.doNotReopenConversationsOnReply,
        disableConversationsStartedByInternalUser: this.selectedTenant.disableConversationsStartedByInternalUser,
        isInternalSupport: this.selectedTenant.isInternalSupport,
        billing: JSON.parse(this.billing),
      });
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      console.log('Error updating tenant: ', e);
    }
  }

  async enableAutomations() {
    try {
      this.isLoading = true;
      await this.tenantsService.enableAutomations(this.selectedTenant.id);
      this.msgService.show('Enabled');
      await this.loadTenants();
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async enableAccounts() {
    try {
      this.isLoading = true;
      await this.tenantsService.enableAccounts(this.selectedTenant.id);

      this.msgService.show('Enabled');
      await this.loadTenants();
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }

  async runAccountSync() {
    try {
      this.isLoading = true;
      await this.tenantsService.runAccountSync(this.selectedTenant.id);
      this.msgService.show('Account sync background tasks queued.');
      this.isLoading = false;
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }
}
